import React, { forwardRef, useCallback, useMemo, useRef, useState } from 'react';

import { AlineTransforms, CANNED_FIELD_METADATA, Select } from '@aline/common';
import { params } from '@aline/routes';
import { Divider, ListItemIcon, ListItemText, Menu, MenuItem, Typography } from '@mui/material';
import type { ExistingFieldRecipient } from 'src/components';
import {
  FieldRecipientAutoComplete,
  getIsSignerFieldRecipient,
  InputFieldButton,
  workflowStepsDialogOpenVar,
} from 'src/components';
import { CANNED_ICON_BY_VARIANT } from 'src/components/icons/CannedFieldIcons';
import { CheckboxFieldIcon } from 'src/components/icons/CheckboxFieldIcon';
import { DateFieldIcon } from 'src/components/icons/DateFieldIcon';
import { DropdownFieldIcon } from 'src/components/icons/DropdownFieldIcon';
import { DynamicBlockIcon } from 'src/components/icons/DynamicBlockIcon';
import { IinBoxIcon } from 'src/components/icons/IinBoxIcon';
import { NumberIcon } from 'src/components/icons/NumberIcon';
import { RadioButtonIcon } from 'src/components/icons/RadioButtonIcon';
import { SignatureFieldIcon } from 'src/components/icons/SignatureFieldIcon';
import { useMaybeDocumentViewer } from 'src/components/viewer/viewer-context';
import { isCI, isDev } from 'src/config';
import { useMaybeEditorRuntime } from 'src/editor/editor-runtime-registry';
import {
  getEditorTabStateActionsForTabId,
  useEditorTabStateSelectorForTabId,
} from 'src/editor/editor-tab-state-registry';
import { useDrawerSearchParam } from 'src/editor/toolbars/right-panel/drawer-search-params';
import { Apollo } from 'src/gql/generated';
import { useGetIsPDFFieldInDocument } from 'src/helpers/input-fields';
import type { CreateFieldForm } from 'src/hooks';
import { useCreateField, useGetCurrentDoc, useGetVariablesForDocument } from 'src/hooks';
import { useMaybeEditor } from 'src/layout-engine';
import { useEditingMode } from 'src/store/document/selectors';
import { makeStyles } from 'tss-react/mui';
import { v4 as uuid4 } from 'uuid';

const SIGNING_CANNED: readonly { text: string; variant: Apollo.CannedFieldVariant }[] = [
  { text: 'Date Signed', variant: Apollo.CannedFieldVariant.DateSigned },
];

const CONTACT_CANNED: readonly { text: string; variant: Apollo.CannedFieldVariant }[] = [
  { text: 'Name', variant: Apollo.CannedFieldVariant.UserName },
  { text: 'Email', variant: Apollo.CannedFieldVariant.UserEmail },
  { text: 'Address', variant: Apollo.CannedFieldVariant.Address },
  { text: 'Title', variant: Apollo.CannedFieldVariant.UserTitle },
  { text: 'Company', variant: Apollo.CannedFieldVariant.Company },
];

const CANNED_DRAG_CY = {
  [Apollo.CannedFieldVariant.Address]:
    Select.document.editor.rightDrawer.addContentPanel.dragCannedAddressButton,
  [Apollo.CannedFieldVariant.Company]:
    Select.document.editor.rightDrawer.addContentPanel.dragCannedCompanyButton,
  [Apollo.CannedFieldVariant.DateSigned]:
    Select.document.editor.rightDrawer.addContentPanel.dragCannedDateSignedButton,
  [Apollo.CannedFieldVariant.UserEmail]:
    Select.document.editor.rightDrawer.addContentPanel.dragCannedUserEmailButton,
  [Apollo.CannedFieldVariant.UserName]:
    Select.document.editor.rightDrawer.addContentPanel.dragCannedUserNameButton,
  [Apollo.CannedFieldVariant.UserTitle]:
    Select.document.editor.rightDrawer.addContentPanel.dragCannedUserTitleButton,
} satisfies Record<Apollo.CannedFieldVariant, string>;

const useStyles = makeStyles<StyleProps>()((theme, { menuWidth }) => ({
  buttonGrid: {
    display: 'grid',
    gap: theme.spacing(1),
    gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
  },
  fieldContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    padding: theme.spacing(2, 1),
  },
  icon: {
    marginRight: theme.spacing(1),
    minWidth: 'unset',
  },
  menuPaper: { width: menuWidth },
  sectionDivider: {
    marginTop: theme.spacing(1),
  },
  sectionHeader: {
    fontWeight: 600,
    marginTop: theme.spacing(1),
  },
}));

interface StyleProps {
  menuWidth?: number;
}

export const AddContentSection = forwardRef(function AddContentSection(): JSX.Element {
  const editor = useMaybeEditor();
  const { document } = useGetCurrentDoc();
  const { variables } = useGetVariablesForDocument(document?.id ?? '', { skip: !document?.id });
  const ref = useRef<HTMLButtonElement>(null);
  const documentViewer = useMaybeDocumentViewer();
  const { editing, editingMode } = useEditingMode();
  const tabId = useMaybeEditorRuntime()?.tabId ?? null;
  const { getIsPDFFieldInDoc } = useGetIsPDFFieldInDocument(tabId);
  const fieldRecipient = useEditorTabStateSelectorForTabId(
    tabId,
    state => state.documentUi.fieldRecipient
  );
  const [menuOpen, setMenuMenuOpen] = useState<boolean>(false);
  const { createField } = useCreateField({
    documentId: document?.id ?? null,
    documentViewer,
    editingMode,
    editor,
    tabId,
  });
  const { classes } = useStyles({ menuWidth: ref?.current?.clientWidth });
  const { setValue: setNewFieldId } = useDrawerSearchParam(params.rightPanel.newFieldId);
  const isAlineDoc = document?.latestVersion.__typename === 'DocumentVersion';
  const isClickToAddFieldEnabled = isDev || isCI;
  const fieldDisabledReason = !editing
    ? 'Switch to Editing to add a field'
    : !fieldRecipient
      ? 'Pick a recipient to add a field'
      : '';
  const checkboxDragField = useMemo(() => {
    const existingCheckboxVariable = fieldRecipient
      ? variables.find(
          variable =>
            !variable.deletedAt &&
            variable.variant === Apollo.MetadataVariant.Checkbox &&
            variable.action?.id === fieldRecipient.id
        )
      : null;

    const shouldInsertExisting =
      existingCheckboxVariable && !getIsPDFFieldInDoc(existingCheckboxVariable.nodeId);

    if (shouldInsertExisting) {
      return {
        __typename: 'DocumentVariable' as const,
        actionType: 'insert' as const,
        checkboxOptions:
          existingCheckboxVariable.answerOptions?.options?.map(option => ({
            choiceRefId: option.choiceRefId,
            id: option.id,
            optionText: option.optionText,
          })) ?? [],
        config: null,
        isDynamic: false,
        label: existingCheckboxVariable.label,
        nodeId: existingCheckboxVariable.nodeId,
        selectedAnswerIds: existingCheckboxVariable.selectedAnswers?.map(answer => answer.id) ?? [],
        value: null,
        variant: Apollo.MetadataVariant.Checkbox,
      };
    }

    return {
      __typename: 'DocumentVariable' as const,
      actionType: 'create' as const,
      config: null,
      isDynamic: false,
      nodeId: uuid4(),
      options: [{ optionText: '', triggerAction: Apollo.TriggerAction.Merge }],
      selectedAnswerIds: [],
      value: null,
      variant: Apollo.MetadataVariant.Checkbox,
    };
  }, [fieldRecipient, getIsPDFFieldInDoc, variables]);

  async function handleFieldButtonClick(form: CreateFieldForm): Promise<void> {
    if (editor?.selection)
      AlineTransforms.nudgeRangeToEdges(editor, {
        at: editor.selection,
      });

    const { field } = await createField(form);
    if (field && form.__typename !== 'SignatureFieldForm') setNewFieldId(field.id);
  }

  const setFieldRecipient = useCallback(
    (fieldRecipient: ExistingFieldRecipient | null): void => {
      if (!tabId) return;
      getEditorTabStateActionsForTabId(tabId).documentUi.setFieldRecipient(fieldRecipient);
    },
    [tabId]
  );

  return (
    <>
      <div className={classes.fieldContainer}>
        <FieldRecipientAutoComplete
          value={fieldRecipient}
          onChange={option => {
            if (!option) return;

            const isNewRecipient = option?.__typename === 'NewFieldRecipient';
            if (isNewRecipient) return workflowStepsDialogOpenVar(true);

            setFieldRecipient(option);
          }}
        />
        <Typography className={classes.sectionHeader}>General</Typography>
        <div className={classes.buttonGrid}>
          <InputFieldButton
            LeftIcon={TextFieldIconWrapper}
            dataCy={Select.document.editor.rightDrawer.addContentPanel.addTextFieldButton}
            disabledReason={fieldDisabledReason}
            dragDataCy={Select.document.editor.rightDrawer.addContentPanel.dragTextFieldButton}
            dragField={{
              __typename: 'DocumentVariable',
              actionType: 'create',
              config: null,
              isDynamic: false,
              nodeId: uuid4(),
              value: null,
              variant: Apollo.MetadataVariant.Text,
            }}
            text="Text"
            onClick={() => {
              if (!isClickToAddFieldEnabled) return;

              return handleFieldButtonClick({
                __typename: 'VariableForm',
                action: fieldRecipient,
                isDynamic: false,
                locationInfo: isAlineDoc
                  ? { __typename: 'DocumentVersion' }
                  : { __typename: 'StaticFileVersion' },
                nodeId: uuid4(),
                value: null,
                variant: Apollo.MetadataVariant.Text,
              });
            }}
          />
          <InputFieldButton
            LeftIcon={DateFieldIconWrapper}
            dataCy={Select.document.editor.rightDrawer.addContentPanel.addDateFieldButton}
            disabledReason={fieldDisabledReason}
            dragDataCy={Select.document.editor.rightDrawer.addContentPanel.dragDateFieldButton}
            dragField={{
              __typename: 'DocumentVariable',
              actionType: 'create',
              config: null,
              isDynamic: false,
              nodeId: uuid4(),
              value: null,
              variant: Apollo.MetadataVariant.Date,
            }}
            text="Date"
            onClick={() => {
              if (!isClickToAddFieldEnabled) return;

              return handleFieldButtonClick({
                __typename: 'VariableForm',
                action: fieldRecipient,
                isDynamic: false,
                locationInfo: isAlineDoc
                  ? { __typename: 'DocumentVersion' }
                  : { __typename: 'StaticFileVersion' },
                nodeId: uuid4(),
                value: null,
                variant: Apollo.MetadataVariant.Date,
              });
            }}
          />
          <InputFieldButton
            LeftIcon={NumberFieldIconWrapper}
            dataCy={Select.document.editor.rightDrawer.addContentPanel.addNumberFieldButton}
            disabledReason={fieldDisabledReason}
            dragDataCy={Select.document.editor.rightDrawer.addContentPanel.dragNumberFieldButton}
            dragField={{
              __typename: 'DocumentVariable',
              actionType: 'create',
              config: null,
              isDynamic: false,
              nodeId: uuid4(),
              value: null,
              variant: Apollo.MetadataVariant.Decimal,
            }}
            text="Number"
            onClick={() => {
              if (!isClickToAddFieldEnabled) return;

              return handleFieldButtonClick({
                __typename: 'VariableForm',
                action: fieldRecipient,
                isDynamic: false,
                locationInfo: isAlineDoc
                  ? { __typename: 'DocumentVersion' }
                  : { __typename: 'StaticFileVersion' },
                nodeId: uuid4(),
                value: null,
                variant: Apollo.MetadataVariant.Decimal,
              });
            }}
          />
          <InputFieldButton
            LeftIcon={DropdownFieldIconWrapper}
            dataCy={Select.document.editor.rightDrawer.addContentPanel.addDropdownFieldButton}
            disabledReason={fieldDisabledReason}
            dragDataCy={Select.document.editor.rightDrawer.addContentPanel.dragDropdownFieldButton}
            dragField={{
              __typename: 'DocumentVariable',
              actionType: 'create',
              config: null,
              isDynamic: false,
              nodeId: uuid4(),
              value: null,
              variant: Apollo.MetadataVariant.Options,
            }}
            text="Dropdown"
            onClick={() => {
              if (!isClickToAddFieldEnabled) return;

              return handleFieldButtonClick({
                __typename: 'VariableForm',
                action: fieldRecipient,
                isDynamic: false,
                locationInfo: isAlineDoc
                  ? { __typename: 'DocumentVersion' }
                  : { __typename: 'StaticFileVersion' },
                nodeId: uuid4(),
                value: null,
                variant: Apollo.MetadataVariant.Options,
              });
            }}
          />
          <InputFieldButton
            LeftIcon={RadioFieldIconWrapper}
            dataCy={Select.document.editor.rightDrawer.addContentPanel.addRadioFieldButton}
            disabledReason={fieldDisabledReason}
            dragDataCy={Select.document.editor.rightDrawer.addContentPanel.dragRadioFieldButton}
            dragField={{
              __typename: 'DocumentVariable',
              actionType: 'create',
              config: null,
              isDynamic: false,
              nodeId: uuid4(),
              value: null,
              variant: Apollo.MetadataVariant.RadioOptions,
            }}
            text="Radio Button"
            onClick={() => {
              if (!isClickToAddFieldEnabled) return;

              return handleFieldButtonClick({
                __typename: 'VariableForm',
                action: fieldRecipient,
                isDynamic: false,
                locationInfo: isAlineDoc
                  ? { __typename: 'DocumentVersion' }
                  : { __typename: 'StaticFileVersion' },
                nodeId: uuid4(),
                value: null,
                variant: Apollo.MetadataVariant.RadioOptions,
              });
            }}
          />
          <InputFieldButton
            LeftIcon={CheckboxFieldIconWrapper}
            dataCy={Select.document.editor.rightDrawer.addContentPanel.addCheckboxFieldButton}
            disabledReason={fieldDisabledReason}
            dragField={checkboxDragField}
            text="Checkbox"
            onClick={() => {
              if (!isClickToAddFieldEnabled) return;

              return handleFieldButtonClick({
                __typename: 'VariableForm',
                action: fieldRecipient,
                isDynamic: false,
                locationInfo: isAlineDoc
                  ? { __typename: 'DocumentVersion' }
                  : { __typename: 'StaticFileVersion' },
                nodeId: uuid4(),
                options: [{ optionText: '', triggerAction: Apollo.TriggerAction.Merge }],
                selectedAnswerIds: [],
                value: null,
                variant: Apollo.MetadataVariant.Checkbox,
              });
            }}
          />
          {isAlineDoc && (
            <InputFieldButton
              ref={ref}
              LeftIcon={DynamicBlockIconWrapper}
              dataCy={Select.document.editor.rightDrawer.addContentPanel.addDynamicBlockButton}
              disabledReason={fieldDisabledReason}
              dragDataCy={Select.document.editor.rightDrawer.addContentPanel.dragDynamicBlockButton}
              dragField={{
                __typename: 'DocumentVariable',
                actionType: 'create',
                config: null,
                isDynamic: true,
                nodeId: uuid4(),
                value: null,
                variant: Apollo.MetadataVariant.Options,
              }}
              text="Dynamic Block"
              onClick={() => setMenuMenuOpen(true)}
            />
          )}
        </div>
        <Divider className={classes.sectionDivider} />
        <Typography className={classes.sectionHeader}>Signing</Typography>
        <div className={classes.buttonGrid}>
          <InputFieldButton
            LeftIcon={SignatureFieldIconWrapper}
            dataCy={Select.document.editor.rightDrawer.addContentPanel.addSignatureButton}
            disabledReason={fieldDisabledReason}
            dragDataCy={Select.document.editor.rightDrawer.addContentPanel.dragSignatureButton}
            dragField={{
              __typename: 'SignatureField',
              nodeId: uuid4(),
            }}
            text="Signature"
            onClick={() => {
              if (
                !isClickToAddFieldEnabled ||
                !fieldRecipient ||
                !getIsSignerFieldRecipient(fieldRecipient)
              )
                return;

              return handleFieldButtonClick({
                __typename: 'SignatureFieldForm',
                action: fieldRecipient,
                locationInfo: isAlineDoc
                  ? { __typename: 'DocumentVersion' }
                  : { __typename: 'StaticFileVersion' },
                nodeId: uuid4(),
              });
            }}
          />
          {SIGNING_CANNED.map(({ text, variant }) => (
            <CannedFieldButton
              key={variant}
              disabledReason={fieldDisabledReason}
              fieldRecipient={fieldRecipient}
              text={text}
              variant={variant}
            />
          ))}
        </div>
        <Divider className={classes.sectionDivider} />
        <Typography className={classes.sectionHeader}>Contact Details</Typography>
        <div className={classes.buttonGrid}>
          {CONTACT_CANNED.map(({ text, variant }) => (
            <CannedFieldButton
              key={variant}
              disabledReason={fieldDisabledReason}
              fieldRecipient={fieldRecipient}
              text={text}
              variant={variant}
            />
          ))}
        </div>
      </div>
      <Menu
        disablePortal
        anchorEl={ref.current}
        anchorOrigin={{ horizontal: 'center', vertical: 'bottom' }}
        classes={{ paper: classes.menuPaper }}
        open={menuOpen}
        transformOrigin={{ horizontal: 'center', vertical: 'top' }}
        onBlur={() => setMenuMenuOpen(false)}
      >
        <MenuItem
          dense
          data-cy={Select.document.editor.rightDrawer.addContentPanel.addDropdownDCMenuItem}
          onClick={async () => {
            return handleFieldButtonClick({
              __typename: 'VariableForm',
              action: fieldRecipient,
              isDynamic: true,
              locationInfo: isAlineDoc
                ? { __typename: 'DocumentVersion' }
                : { __typename: 'StaticFileVersion' },
              nodeId: uuid4(),
              value: null,
              variant: Apollo.MetadataVariant.Options,
            });
          }}
        >
          <ListItemIcon className={classes.icon}>
            <DropdownFieldIcon />
          </ListItemIcon>
          <ListItemText primary="Dropdown" />
        </MenuItem>
        <MenuItem
          dense
          data-cy={Select.document.editor.rightDrawer.addContentPanel.addRadioDCMenuItem}
          onClick={() => {
            return handleFieldButtonClick({
              __typename: 'VariableForm',
              action: fieldRecipient,
              isDynamic: true,
              locationInfo: isAlineDoc
                ? { __typename: 'DocumentVersion' }
                : { __typename: 'StaticFileVersion' },
              nodeId: uuid4(),
              value: null,
              variant: Apollo.MetadataVariant.RadioOptions,
            });
          }}
        >
          <ListItemIcon className={classes.icon}>
            <RadioButtonIcon />
          </ListItemIcon>
          <ListItemText primary="Radio" />
        </MenuItem>
      </Menu>
    </>
  );
});

function TextFieldIconWrapper() {
  return <IinBoxIcon />;
}

function DateFieldIconWrapper() {
  return <DateFieldIcon />;
}

function NumberFieldIconWrapper() {
  return <NumberIcon />;
}

function DropdownFieldIconWrapper() {
  return <DropdownFieldIcon />;
}

function CheckboxFieldIconWrapper() {
  return <CheckboxFieldIcon />;
}

function RadioFieldIconWrapper() {
  return <RadioButtonIcon />;
}

function SignatureFieldIconWrapper({ disabled }: { disabled?: boolean }) {
  return <SignatureFieldIcon stroke={disabled ? 'currentColor' : undefined} />;
}

function DynamicBlockIconWrapper() {
  return <DynamicBlockIcon />;
}

interface CannedFieldButtonProps {
  disabledReason: string;
  fieldRecipient: ExistingFieldRecipient | null;
  text: string;
  variant: Apollo.CannedFieldVariant;
}

function CannedFieldButton({
  disabledReason,
  fieldRecipient,
  text,
  variant,
}: CannedFieldButtonProps): JSX.Element {
  return (
    <InputFieldButton
      LeftIcon={CANNED_ICON_BY_VARIANT[variant]}
      dataCy={`add-canned-${variant}`}
      disabledReason={disabledReason}
      dragDataCy={CANNED_DRAG_CY[variant]}
      dragField={{
        __typename: 'CannedField',
        assigneeId: fieldRecipient?.id ?? null,
        nodeId: uuid4(),
        variant,
      }}
      helpText={CANNED_FIELD_METADATA[variant].authorHelpText}
      text={text}
    />
  );
}
